import { Suspense, useState, useEffect, useRef, useMemo } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls, Center } from "@react-three/drei";
import { OBJLoader } from "three-stdlib";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { useSelector, useDispatch } from 'react-redux';
import { DirectionalLightTarget } from "@react-three/fiber";
import * as Sentry from "@sentry/react";
import { getNetworkInfo, getNetworkSpeedCategory } from "../../utils/networkSpeed";

import * as THREE from "three";
import { setLoading, setProgress } from "../../redux/modelSlice";
import { companyType } from "colors-config";

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
console.log('hello', isMobile)
const CameraHandler = ({ targetPosition, reset, setReset }) => {
  const { camera } = useThree();
  const isAnimating = useRef(false);
  const animationFrameId = useRef(null);

  const {
    lowerAngle,
    upperAngle,
    leftAngle,
    rightAngle
  } = useSelector((state) => state.model)

  
  useEffect(() => {
    if (!targetPosition) return;

    if (isMobile) {
      camera.position.copy(targetPosition); // Instantly move the camera
      camera.lookAt(0, 0, 0);
      return;
    }

    // Prevent multiple animations
    if (isAnimating.current) return;
    isAnimating.current = true;

    const animateCamera = () => {
      camera.position.lerp(targetPosition, 1);
      camera.lookAt(0, 0, 0);


      if (camera.position.distanceTo(targetPosition) > 0.5) {
        setTimeout(() => {
          animationFrameId.current = requestAnimationFrame(animateCamera);
        }, 16); // ~60 FPS (16ms per frame)
      } else {
        isAnimating.current = false;
      }
    };

    animateCamera();

    // Cleanup function to cancel the animation frame
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      isAnimating.current = false;
    };
  }, [targetPosition, camera]);


  useEffect(() => {
    if (reset) {
      if (upperAngle) {
        camera.position.set(0, -250, 5);
      } else if (lowerAngle) {
        camera.position.set(0, 220, 3);
      } else if (leftAngle) {
        camera.position.set(-250, 35, 0);
      } else if (rightAngle) {
        camera.position.set(250, 35, 0);
      }
      else {
        camera.position.set(0, 35, 250); // Reset to original position
      }
      camera.rotation.set(0, 0, 0); // Reset rotation
      setReset(false);
    }
  }, [reset, camera, setReset]);

  return null; // No JSX needed, it only updates the camera
};

function DynamicFrontalLighting() {
  

  return (
    <>
      {/* Hemisphere Light */}
      <hemisphereLight
        color="#ffffff"
        groundColor="#b97a20"
        intensity={0.6}
      />

      {/* Directional Lights */}
      <directionalLight
        castShadow
        position={[50, -10, 0]}
        intensity={3.5}
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
      />
      <directionalLight
        castShadow
        position={[-30, -10, 0]}
        intensity={2.5}
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
      />
      <directionalLight
        castShadow
        position={[-10, -10, 30]}
        intensity={3.5}
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
      />
      
      <directionalLight
        castShadow
        position={[0, 50, 0]}
        intensity={2.5}
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
      />

      <directionalLight
        castShadow
        position={[0, 0, 50]}
        intensity={1.5}
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
      />

      <directionalLight
        castShadow
        position={[0, 0, -50]}
        intensity={1.5}
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
      />
      {/* Ambient Light */}
      <ambientLight intensity={0.4} />

      {/* Point Light (Optional) */}
      <pointLight
        position={[0, 10, 0]}
        intensity={0.5}
        color="#ffffff"
      />
    </>
  );
}

const Grid = ({ gridSize }) => {
  const { camera, viewport } = useThree();
  const lineSegmentsRef = useRef();
  const bufferRef = useRef();
  const [positions, setPositions] = useState(new Float32Array([]));

  useEffect(() => {
    const divisions = 100;
    const size = viewport.width / gridSize;
    const step = (size * 2) / divisions;

    const lines = [];
    for (let i = 0; i <= divisions; i++) {
      const x = -size + i * step;
      lines.push(x, -size, 0, x, size, 0);
      lines.push(-size, x, 0, size, x, 0);
    }

    const newPositions = new Float32Array(lines);
    setPositions(newPositions);

    if (bufferRef.current) {
      bufferRef.current.array = newPositions;
      bufferRef.current.needsUpdate = true;
    }
  }, [gridSize, viewport.width]);

  useFrame(() => {
    if (lineSegmentsRef.current) {
      const distance = 30;
      const cameraDistance = camera.position.distanceTo(new THREE.Vector3(0, 0, 0));
      const scaleFactor = 100 / cameraDistance;
      lineSegmentsRef.current.scale.set(scaleFactor, scaleFactor, scaleFactor);
      lineSegmentsRef.current.quaternion.copy(camera.quaternion);

      const offset = new THREE.Vector3(0, 0, -distance);
      offset.applyQuaternion(camera.quaternion);
      lineSegmentsRef.current.position.copy(camera.position).add(offset);
    }
  });

  return (
    <lineSegments ref={lineSegmentsRef}>
      <bufferGeometry attach="geometry">
        <bufferAttribute
          ref={bufferRef} // Reference to update buffer explicitly
          attach="attributes-position"
          count={positions.length / 3}
          array={positions}
          itemSize={3}
          needsUpdate
        />
      </bufferGeometry>
      <lineBasicMaterial attach="material" color="#000000" />
    </lineSegments>
  );
};


const DraggableGroup = ({ upperModel, lowerModel }) => {
  const groupRef = useRef();
  const { camera, raycaster, mouse } = useThree();
  const [isDragging, setIsDragging] = useState(false);
  const offset = useRef(new THREE.Vector3());

  // Pointer Down: Start Dragging
  const onPointerDown = (event) => {
    event.stopPropagation();
    setIsDragging(true);

    // Calculate offset between mouse and group position
    const intersection = raycaster.intersectObject(groupRef.current, true);
    if (intersection.length > 0) {
      offset.current.copy(intersection[0].point).sub(groupRef.current.position);
    }
  };

  // Pointer Up: Stop Dragging
  const onPointerUp = () => {
    setIsDragging(false);
  };

  useFrame(() => {
    if (isDragging) {
      raycaster.setFromCamera(mouse, camera);
      const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0); 
      const intersection = new THREE.Vector3();

      if (raycaster.ray.intersectPlane(plane, intersection)) {
        groupRef.current.position.set(intersection.x - offset.current.x, intersection.y - offset.current.y, 0);
      }
    }
  });

  return (
    <group
      ref={groupRef}
      position={[0, 0, 0]}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
    >
      {upperModel && <primitive object={upperModel} />}
      {lowerModel && <primitive object={lowerModel} />}
    </group>
  );
};



export default function TeethModel({ targetPosition, reset, setReset }) {
  const [models, setModels] = useState({ upper: [], lower: [] });
  const [selectedOption, setSelectedOption] = useState(6);
  const [loadingStartTime, setLoadingStartTime] = useState(null);
  const [hasTrackedPerformance, setHasTrackedPerformance] = useState(false);
  const [networkInfo, setNetworkInfo] = useState(null);

  const dispatch = useDispatch();

  const {
    showLowerModel,
    showUpperModel,
    position,
    lowerModelIndex,
    upperModelIndex,
    meshed,
    lowerAlignerFiles,
    upperAlignerFiles,
    loading,
    progress,
    showIpr,
    noOfUpperAligners,
    noOfLowerAligners,
    patientDetails,
  } = useSelector((state) => state.model);

  const zeroStage = () => {
    if((lowerAlignerFiles.length + upperAlignerFiles.length) > (noOfUpperAligners + noOfLowerAligners)){
      return 0;
    }
    return 1;
  }


  const handleDropdownChange = (event) => {
    setSelectedOption(event.target.value);
  };

  const autoAlignModel = (model) => {
    // model.traverse((child) => {
    //   if (child.isMesh) {
    //     child.geometry.computeBoundingBox();
    //     child.geometry.computeVertexNormals();

    //     const bbox = child.geometry.boundingBox;
    //     const size = new THREE.Vector3();
    //     bbox.getSize(size); // Get model size

    //     // Adjust position: center it based on bounding box
    //     const center = new THREE.Vector3();
    //     bbox.getCenter(center);
    //     // child.geometry.translate(center.x, center.y, center.z);

    //     // Try to orient it upwards based on the longest axis
    //     const { x, y, z } = size;
    //     if (size.y > size.z) {
    //       model.rotation.set(-Math.PI / 2, 0, 0); // Rotate to stand upright
    //     }
    //   }
    // });

    model.scene.traverse((child) => {
      if (child.isMesh) {
          child.material.color.convertSRGBToLinear(); 
      }
  });
  scene.add(model.scene);

    return model;
  };


  // Preload all models

  useEffect(() => {
    // Start performance timer and get network info when loading begins
    if (!loadingStartTime && (upperAlignerFiles.length > 0 || lowerAlignerFiles.length > 0)) {
      setLoadingStartTime(performance.now());
      setHasTrackedPerformance(false);
      
      // Get network information at the start of loading
      console.log('🔍 TeethModel: About to call getNetworkInfo...');
      
      getNetworkInfo()
        .then(info => {
          console.log('✅ TeethModel: Network info received:', info);
          setNetworkInfo(info);
        })
        .catch(error => {
          console.error('❌ TeethModel: Network info failed:', error);
          setNetworkInfo({ 
            error: 'failed_to_detect',
            error_message: error?.message || 'Unknown error',
            error_stack: error?.stack || 'No stack trace',
            timestamp: new Date().toISOString(),
            online_status: navigator.onLine
          });
        });
    }

    const totalFiles = upperAlignerFiles.length + lowerAlignerFiles.length;
    let loadedFiles = 0;

    const increaseProgress = () => {
      setProgress((prev) => {
        if (prev < 85) {
          return prev + (85 - prev) * 0.02; // Smooth acceleration
        }
        return prev; // Stop at 85% until files are loaded
      });
    };

    const interval = setInterval(increaseProgress, 50);

    // Increase every 100ms

    const loadModels = async () => {
      const loader = new GLTFLoader();

      // Set up DRACOLoader
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath("/draco/"); // Path to Draco decoder files
      loader.setDRACOLoader(dracoLoader);
      try {
        // Load upper aligner files
        const upperPromises = upperAlignerFiles.map(
          (file) =>
            new Promise((resolve, reject) => {
              loader.load(
                file,
                (model) => {
                 
                  loadedFiles += 1; // Increment loaded files
                  const calculatedProgress = Math.min(85, Math.floor((loadedFiles / totalFiles) * 85));
                  dispatch(setProgress(calculatedProgress));
                  resolve(model.scene); // Resolve the model
                },
                undefined, // No specific progress tracking for individual files
                (error) => reject(error) // Handle errors
              );
            })
        );

        // Load lower aligner files
        const lowerPromises = lowerAlignerFiles.map(
          (file) =>
            new Promise((resolve, reject) => {
              loader.load(
                file,
                (model) => {
                  
                  loadedFiles += 1; // Increment loaded files
                  const calculatedProgress = Math.min(85, Math.floor((loadedFiles / totalFiles) * 85));
                  dispatch(setProgress(calculatedProgress));
                  resolve(model.scene); // Resolve the model
                },
                undefined,
                (error) => reject(error)
              );
            })
        );

        const [upperModels, lowerModels] = await Promise.all([
          Promise.all(upperPromises),
          Promise.all(lowerPromises),
        ]);

        setModels({
          upper: upperModels,
          lower: lowerModels,
        });

        clearInterval(interval);
        if (upperModels.length || lowerModels.length) {
          dispatch(setProgress(100)); // Set to 100% when all models are loaded
          
          // Track performance and send to Sentry
          if (loadingStartTime && !hasTrackedPerformance && companyType === 5) {
            const endTime = performance.now();
            const duration = (endTime - loadingStartTime)/1000.0;
            
            // Prepare network data for Sentry
            const networkData = networkInfo && !networkInfo.error ? {
              network_connection_type: networkInfo.connection_type,
              network_downlink_mbps: networkInfo.downlink_mbps,
              network_rtt_ms: networkInfo.rtt_ms,
              network_save_data: networkInfo.save_data_mode,
              network_type: networkInfo.network_type,
              network_speed_category: getNetworkSpeedCategory(networkInfo),
              network_online_status: networkInfo.online_status,
              measured_speed_mbps: networkInfo.measured_speed_mbps,
              speed_test_duration_ms: networkInfo.speed_test_duration_ms,
              speed_test_error: networkInfo.speed_test_error,
              has_connection_api: networkInfo.has_connection_api,
              supports_performance_api: networkInfo.supports_performance_api,
              detection_errors: networkInfo.detection_errors
            } : {
              network_connection_type: 'unknown',
              network_online_status: navigator.onLine,
              network_detection_error: networkInfo?.error || 'failed_to_get_info',
              network_error_message: networkInfo?.error_message || 'Unknown error',
              network_debug_info: {
                has_network_info: !!networkInfo,
                network_info_keys: networkInfo ? Object.keys(networkInfo) : [],
                navigator_online: navigator.onLine,
                has_connection_api: !!(navigator.connection || navigator.mozConnection || navigator.webkitConnection),
                has_performance_api: !!window.performance
              }
            };
            
            // Capture Sentry event with performance and network data
            // Sentry.captureEvent({
            //   message: "tp_viewer_render_time",
            //   level: "info",
            //   extra: {
            //     duration_ms: duration.toFixed(2),
            //     total_models_loaded: upperModels.length + lowerModels.length,
            //     upper_models_count: upperModels.length,
            //     lower_models_count: lowerModels.length,
            //     page_url: window.location.href,
            //     user_agent: navigator.userAgent,
            //     is_mobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
            //     ...networkData
            //   },
            //   tags: {
            //     component: "TeethModel",
            //     action: "model_loading",
            //     network_type: networkData.network_connection_type,
            //     network_speed: networkData.network_speed_category || 'unknown'
            //   }
            // });
            Sentry.startSpan(
              {
                name: "tp_viewer_loading_time",
                op: "file.tp_viewer_loading_time",
                attributes: {
                  loading_duration_ms: Number(duration.toFixed(2)),
                  total_models_loaded: upperModels.length + lowerModels.length,
                  upper_models_count: upperModels.length,
                  lower_models_count: lowerModels.length,
                  page_url: window.location.href,
                  user_agent: navigator.userAgent,
                  is_mobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
                  loading_patient_id: patientDetails.patient_id,
                  ...networkData,
                },
              },
              () => {
                Sentry.captureMessage("tp_viewer_loading_time", {
                  level: "info",
                  tags: {
                    loading_duration_ms: Number(duration.toFixed(2)),
                    total_models_loaded: upperModels.length + lowerModels.length,
                    upper_models_count: upperModels.length,
                    lower_models_count: lowerModels.length,
                    page_url: window.location.href,
                    user_agent: navigator.userAgent,
                    is_mobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
                    loading_patient_id: patientDetails.patient_id,
                    ...networkData,
                  },
                  extra: {
                    loading_duration_ms: Number(duration.toFixed(2)),
                    total_models_loaded: upperModels.length + lowerModels.length,
                    upper_models_count: upperModels.length,
                    lower_models_count: lowerModels.length,
                    page_url: window.location.href,
                    user_agent: navigator.userAgent,
                    is_mobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
                    loading_patient_id: patientDetails.patient_id,
                    ...networkData,
                  },
                  transaction: true,
                });
              });
            
            console.log('🔍 TeethModel===>: Sentry message sent:', {
              message: "tp_viewer_loading_time",
              level: "info",
              tags: {
                loading_duration_ms: Number(duration.toFixed(2)),
                total_models_loaded: upperModels.length + lowerModels.length,
                upper_models_count: upperModels.length,
                lower_models_count: lowerModels.length,
                page_url: window.location.href,
                user_agent: navigator.userAgent,
                is_mobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
                loading_patient_id: patientDetails.patient_id,
                ...networkData,
              }})
            setHasTrackedPerformance(true);
          }
        }
        dispatch(setLoading(false));

      } catch (error) {
        console.error("Error loading models:", error);
      }
    };

    loadModels();
    return () => clearInterval(interval);

  }, [lowerAlignerFiles, upperAlignerFiles, loadingStartTime]);

  return (
    <div className={`canvas-container ${!showIpr ? "expanded-canvas" : ""}`}
      style={{ position: "relative" }}>
      {progress == 100 && <div>
        {meshed && (
          <div
            style={{
              position: "absolute",
              top: -20,
              right: 0,
              zIndex: 10, // Dropdown on top of the canvas
            }}
          >
            <select
              value={selectedOption}
              onChange={handleDropdownChange}
              className="custom-dropdown"
            >
              <option value="6">1 mm</option>
              <option value="4">3 mm</option>
              <option value="2">5 mm</option>
            </select>
          </div>
        )}
        {/* Canvas with Grid */}
        <Canvas shadows camera={{ position: [0, 35, 250], fov: isMobile ? 20 : 25 }}   >
          {/* Lighting */}
          <ambientLight intensity={0.2} />
          <DynamicFrontalLighting />
          {/* <directionalLight castShadow intensity={2.3} position={[0, 6, 11]} /> */}
          {/* <spotLight castShadow intensity={1} position={[5, 2, 5]} angle={0.2} penumbra={0.2} /> */}
          {/* <pointLight castShadow intensity={1} position={[10, 10, 10]} color={new THREE.Color("red")} /> */}

          {/* Grid inside Canvas */}
          {meshed && selectedOption ? <Grid gridSize={selectedOption} /> : null}

          {/* 3D Models */}
          <Suspense fallback={null}>
            <Center>
            {showUpperModel && models.upper[upperModelIndex - zeroStage()] &&
                <primitive object={models.upper[upperModelIndex - zeroStage()]} scale={1} position={[0, 0, 0]} />
              }
              {showLowerModel && models.lower[lowerModelIndex - zeroStage()] &&
                <primitive object={models.lower[lowerModelIndex - zeroStage()]} scale={1} position={[0, 0, 0]} />
              }
              {/* {showUpperModel && showLowerModel && models.upper[upperModelIndex - 1] && models.lower[lowerModelIndex - 1] ? (
                <DraggableGroup
                  upperModel={models.upper[upperModelIndex - 1]}
                  lowerModel={models.lower[lowerModelIndex - 1]}
                />
              ) : (
                <>
                  {showUpperModel && models.upper[upperModelIndex - 1] && (
                    <primitive object={models.upper[upperModelIndex - 1]} />
                  )}
                  {showLowerModel && models.lower[lowerModelIndex - 1] && (
                    <primitive object={models.lower[lowerModelIndex - 1]} />
                  )}
                </>
              )} */}
            </Center>
          </Suspense>

          <CameraHandler targetPosition={targetPosition} reset={reset} setReset={setReset} />
          {/* Orbit Controls */}
          <OrbitControls minDistance={isMobile ? 80: 70} maxDistance={300} enablePan={true} rotateSpeed={0.2} />
        </Canvas>
      </div>}
    </div>
  );
}
